import {MyRequirementsMockup} from 'testUtils/dashboardMockups/MyRequirementsMockup';
import {useGetMyRequirementsQuery} from './myRequirements.generated';
import {KeywordStatus, WorkflowStatus} from 'types';
import {
	useGetVexWorkbenchItemsQuery,
	useGetWorkbenchBaselinesQuery,
} from './workbenchItems.generated';
import {
	GetMyRequirementsReviewQuery,
	useGetMyRequirementsReviewQuery,
} from './myRequirementsReview.generated';
import {MyRequirementsReviewMockup} from 'testUtils/dashboardMockups/MyRequirementsReviewMockup';
import {useGetActivityItemsQuery} from 'components/ActivityList/hooks/activityItems.generated';
import {useUserContext} from 'authentication/UserContext';
import {ActivityItemsMockup} from 'testUtils/dashboardMockups/ActivityItemsMockup';
import {
	VkoApproverDashboardItemType,
	VkoApproverItemsMockup,
} from 'testUtils/dashboardMockups/VkoApproverItems';
import {useGetVkoApproverWorkbenchRegulatoryDocumentsQuery} from './vkoApproverWorkbenchRegulatoryDocuments.generated';
import {VexWorkbenchItemsSimplifiedMockup} from 'testUtils/dashboardMockups/WorkbenchItemsVexMockup';

/*
  This Hook is used for local test purposes only. Don't commit this flag set to true!
*/

const IS_DEBUG = false;
const DEBUG_MULTIPLIER = 1;

export const useDashboardQueries = () => {
	const {
		localAccountId,
		isAdmin,
		isVex,
		isVko,
		isApprover,
		isBedarfskoordinator,
		isAvk,
		isBav,
	} = useUserContext();
	const {data: myReqData, loading: myReqLoading} = useGetMyRequirementsQuery({
		skip: !isAdmin && !isVex,
	});

	const {data: myRequirementsData, loading: myRequirementsLoading} =
		useGetMyRequirementsReviewQuery({skip: !isAdmin && !isApprover});

	const {
		data: vexWorkbenchItemsFinalized,
		loading: loadingVexWorkbenchItemsFinalized,
	} = useGetVexWorkbenchItemsQuery({
		variables: {
			workflowStatus: WorkflowStatus.Finalized,
			keywordStatus: KeywordStatus.Finalized,
		},
		skip: !isAdmin && !isVex,
	});

	const {
		data: vexWorkbenchItemsAccepted,
		loading: loadingVexWorkbenchItemsAccepted,
	} = useGetVexWorkbenchItemsQuery({
		variables: {
			workflowStatus: WorkflowStatus.Finalized,
			keywordStatus: KeywordStatus.Accepted,
		},
		skip: !isAdmin && !isVex,
	});

	const {data: vexWorkbenchItemsNew, loading: loadingVexWorkbenchItemsNew} =
		useGetVexWorkbenchItemsQuery({
			variables: {
				workflowStatus: WorkflowStatus.Finalized,
				keywordStatus: KeywordStatus.New,
			},
			skip: !isAdmin && !isVex,
		});

	const {data: vkoApproverRegDocData, loading: vkoApproverRegDocLoading} =
		useGetVkoApproverWorkbenchRegulatoryDocumentsQuery({
			skip: !isAdmin && !(isVko && isApprover),
		});

	const {data: activityItemsData, loading: loadingActivityItems} =
		useGetActivityItemsQuery({
			variables: {userId: localAccountId || ''},
			skip: isAvk || isBav || !localAccountId,
		});

	const {data: baselinesData, loading: loadingBaselines} =
		useGetWorkbenchBaselinesQuery({
			skip: !isBedarfskoordinator,
		});
	const getRequirementData = () => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: MyRequirementsMockup,
			};
		}
		return {
			data: myReqData,
			loading: myReqLoading,
		};
	};

	const getVexWorkbenchItemsQuery = (
		status:
			| KeywordStatus.Finalized
			| KeywordStatus.Accepted
			| KeywordStatus.New,
	) => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: VexWorkbenchItemsSimplifiedMockup,
			};
		}
		switch (status) {
			case KeywordStatus.Finalized:
				return {
					loading: loadingVexWorkbenchItemsFinalized,
					data: vexWorkbenchItemsFinalized,
				};
			case KeywordStatus.Accepted:
				return {
					loading: loadingVexWorkbenchItemsAccepted,
					data: vexWorkbenchItemsAccepted,
				};
			case KeywordStatus.New:
			default:
				return {
					loading: loadingVexWorkbenchItemsNew,
					data: vexWorkbenchItemsNew,
				};
		}
	};

	const getMyRequirementsReviewQuery = (): {
		loading: boolean;
		data: GetMyRequirementsReviewQuery | undefined;
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: MyRequirementsReviewMockup,
			};
		}
		return {
			loading: myRequirementsLoading,
			data: myRequirementsData,
		};
	};

	const getActivityItems = () => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: ActivityItemsMockup,
			};
		}
		return {
			loading: loadingActivityItems,
			data: activityItemsData,
		};
	};

	const getBaseLineItems = () => {
		return {
			loading: loadingBaselines,
			data: baselinesData,
		};
	};

	const getVkoApproverTodoItems = (): {
		loading: boolean;
		data: VkoApproverDashboardItemType[];
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: VkoApproverItemsMockup.regulatoryDocuments
					.filter(item =>
						[
							WorkflowStatus.New,
							WorkflowStatus.InProgressInternal,
							WorkflowStatus.Modified,
							WorkflowStatus.Clearing,
						].includes(item.workflow.status),
					)
					.sort(
						(
							rd1: VkoApproverDashboardItemType,
							rd2: VkoApproverDashboardItemType,
						) => (rd1.createdAt > rd2.createdAt ? 1 : -1),
					),
			};
		}
		return {
			loading: vkoApproverRegDocLoading,
			data:
				vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
					?.todoItems || [],
		};
	};

	const getVkoApproverInputItems = (): {
		loading: boolean;
		data: VkoApproverDashboardItemType[];
	} => {
		if (IS_DEBUG) {
			return {
				loading: false,
				data: VkoApproverItemsMockup.regulatoryDocuments
					.filter(item =>
						[
							WorkflowStatus.InProgressInternal,
							WorkflowStatus.Modified,
							WorkflowStatus.Finalized,
							WorkflowStatus.Clearing,
						].includes(item.workflow.status),
					)
					.sort(
						(
							rd1: VkoApproverDashboardItemType,
							rd2: VkoApproverDashboardItemType,
						) => (rd1.createdAt > rd2.createdAt ? 1 : -1),
					),
			};
		}
		return {
			loading: vkoApproverRegDocLoading,
			data:
				vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
					?.inputItems || [],
		};
	};

	const getVkoApproverMyScopeDocCount = (): number | undefined => {
		return vkoApproverRegDocData?.vkoApproverWorkbenchRegulatoryDocuments
			?.myScopeDocCount;
	};

	return {
		myRequirementsReturnData: getRequirementData(),
		getMyRequirementsReviewQuery,
		getActivityItems,
		getBaseLineItems,
		getVkoApproverTodoItems,
		getVkoApproverInputItems,
		getVkoApproverMyScopeDocCount,
		getVexWorkbenchItemsQuery,
	};
};
